// Qué fotos de una carpeta están marcadas en "Elegí qué agregar" y qué chip sale de eso —
// ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes.
//
// Una carpeta es UN chip (el puntero `kind: 'moodboard'`), no una referencia por foto: lo
// que se marca adentro de la carpeta termina en su `fotoIds`. El chip es la fuente de
// verdad —el nuevo, o el que ya estaba en la caja—, así que no hay un segundo estado de
// "fotos marcadas" que se pueda desincronizar.
//
// Puro, para poder probarlo.

import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { adjuntoDeCarpetaDeMoodboard } from './bibliotecaDeAdjuntos';

/** Las fotos marcadas de una carpeta según su chip: sin chip, ninguna; sin `fotoIds`, todas. */
export function fotosMarcadas(
  chip: ChatAttachment | null | undefined,
  fotosDeLaCarpeta: readonly { id: string }[],
): Set<string> {
  if (!chip || chip.kind !== 'moodboard') return new Set();
  return new Set(chip.fotoIds ?? fotosDeLaCarpeta.map((f) => f.id));
}

/** Marca o desmarca una foto. */
export function alternarFoto(marcadas: ReadonlySet<string>, fotoId: string): Set<string> {
  const next = new Set(marcadas);
  if (next.has(fotoId)) next.delete(fotoId);
  else next.add(fotoId);
  return next;
}

/** "Elegir toda la carpeta" o, si ya estaban todas, "Quitar todas". */
export function alternarTodas(
  marcadas: ReadonlySet<string>,
  fotosDeLaCarpeta: readonly { id: string }[],
): Set<string> {
  return estanTodas(marcadas, fotosDeLaCarpeta) ? new Set() : new Set(fotosDeLaCarpeta.map((f) => f.id));
}

export const estanTodas = (marcadas: ReadonlySet<string>, fotosDeLaCarpeta: readonly { id: string }[]): boolean =>
  fotosDeLaCarpeta.length > 0 && fotosDeLaCarpeta.every((f) => marcadas.has(f.id));

/**
 * El chip de una carpeta con estas fotos marcadas, en el orden de la carpeta (es el de
 * `ver_moodboard`, que numera por él); `null` si no quedó ninguna. La miniatura es la
 * primera elegida: la portada de la carpeta puede ser una que se desmarcó.
 */
export function chipDeCarpeta(
  carpeta: { id: string; nombre: string },
  fotosDeLaCarpeta: readonly { id: string; thumbUrl: string }[],
  marcadas: ReadonlySet<string>,
): ChatAttachment | null {
  const elegidas = fotosDeLaCarpeta.filter((f) => marcadas.has(f.id));
  if (elegidas.length === 0) return null;
  return adjuntoDeCarpetaDeMoodboard({
    id: carpeta.id,
    nombre: carpeta.nombre,
    cantidadFotos: fotosDeLaCarpeta.length,
    portada: [elegidas[0].thumbUrl],
    fotoIds: elegidas.map((f) => f.id),
  });
}
